import { Component, OnInit } from '@angular/core';

interface LoadBalancer {
  id: string;
  name: string;
  status: 'Active' | 'Inactive' | 'Provisioning' | 'Error';
  ipAddress: string;
  region: string;
  type: 'Application' | 'Network';
  ports: number[];
  backendServers: number;
  uptime: string;
  created: Date;
  metrics: {
    requestsPerSecond: number[];
    latency: number[];
    connections: number[];
  };
  logs: LogEntry[];
}

interface LogEntry {
  timestamp: Date;
  level: 'Info' | 'Warning' | 'Error';
  message: string;
}

@Component({
  selector: 'app-load-balancers',
  templateUrl: './load-balancers.component.html',
  styleUrls: ['./load-balancers.component.css']
})
export class LoadBalancersComponent implements OnInit {

  loadBalancers: LoadBalancer[] = [];
  filteredLoadBalancers: LoadBalancer[] = [];
  selectedLoadBalancer: LoadBalancer | null = null;
  
  // Pagination
  currentPage = 1;
  pageSize = 5;
  totalPages = 1;
  
  // Search and Filter
  searchTerm = '';
  statusFilter = 'All';

  // Modal
  isCreateModalOpen = false;
  newLbName = '';
  newLbType = 'Application';
  newLbRegion = 'us-east-1';

  // Detailed View Tabs
  activeTab = 'overview'; // 'overview', 'metrics', 'logs'

  constructor() { }

  ngOnInit(): void {
    this.loadMockData();
    this.applyFilters();
  }

  loadMockData() {
    // Generate some realistic mock data
    const statuses: ('Active' | 'Inactive' | 'Provisioning' | 'Error')[] = ['Active', 'Active', 'Active', 'Inactive', 'Provisioning'];
    const types: ('Application' | 'Network')[] = ['Application', 'Application', 'Network', 'Application', 'Network'];
    const regions = ['us-east-1', 'us-west-2', 'eu-central-1', 'ap-southeast-1'];

    for (let i = 1; i <= 15; i++) {
      this.loadBalancers.push({
        id: `lb-${1000 + i}`,
        name: `web-prod-lb-${i}`,
        status: statuses[i % 5],
        ipAddress: `203.0.113.${10 + i}`,
        region: regions[i % 4],
        type: types[i % 5],
        ports: i % 2 === 0 ? [80, 443] : [8080],
        backendServers: Math.floor(Math.random() * 10) + 2,
        uptime: `${Math.floor(Math.random() * 30) + 1}d ${Math.floor(Math.random() * 23)}h`,
        created: new Date(Date.now() - Math.random() * 10000000000),
        metrics: {
          requestsPerSecond: Array.from({length: 10}, () => Math.floor(Math.random() * 1000) + 500),
          latency: Array.from({length: 10}, () => Math.floor(Math.random() * 50) + 10),
          connections: Array.from({length: 10}, () => Math.floor(Math.random() * 5000) + 1000),
        },
        logs: [
          { timestamp: new Date(), level: 'Info', message: 'Health check passed' },
          { timestamp: new Date(Date.now() - 3600000), level: 'Info', message: 'Configuration updated' },
          { timestamp: new Date(Date.now() - 7200000), level: 'Warning', message: 'High latency detected on backend-01' },
          { timestamp: new Date(Date.now() - 86400000), level: 'Info', message: 'Load balancer started' }
        ]
      });
    }
    
    // Select the first one by default
    if (this.loadBalancers.length > 0) {
      this.selectedLoadBalancer = this.loadBalancers[0];
    }
  }

  applyFilters() {
    let temp = this.loadBalancers;

    // Search
    if (this.searchTerm) {
      const term = this.searchTerm.toLowerCase();
      temp = temp.filter(lb => 
        lb.name.toLowerCase().includes(term) || 
        lb.ipAddress.includes(term) ||
        lb.id.toLowerCase().includes(term)
      );
    }

    // Status Filter
    if (this.statusFilter !== 'All') {
      temp = temp.filter(lb => lb.status === this.statusFilter);
    }

    // Pagination Logic
    this.totalPages = Math.ceil(temp.length / this.pageSize);
    // Ensure current page is valid
    if (this.currentPage > this.totalPages) {
        this.currentPage = Math.max(1, this.totalPages);
    }
    
    const startIndex = (this.currentPage - 1) * this.pageSize;
    this.filteredLoadBalancers = temp.slice(startIndex, startIndex + this.pageSize);
  }

  onSearch(event: any) {
    this.searchTerm = event.target.value;
    this.currentPage = 1;
    this.applyFilters();
  }

  onStatusFilterChange(status: string) {
    this.statusFilter = status;
    this.currentPage = 1;
    this.applyFilters();
  }

  selectLoadBalancer(lb: LoadBalancer) {
    this.selectedLoadBalancer = lb;
    this.activeTab = 'overview';
  }

  prevPage() {
    if (this.currentPage > 1) {
      this.currentPage--;
      this.applyFilters();
    }
  }

  nextPage() {
    if (this.currentPage < this.totalPages) {
      this.currentPage++;
      this.applyFilters();
    }
  }

  openCreateModal() {
    this.isCreateModalOpen = true;
  }

  closeCreateModal() {
    this.isCreateModalOpen = false;
    this.newLbName = '';
  }

  createLoadBalancer() {
    // Simulate creation
    const newLb: LoadBalancer = {
      id: `lb-${Math.floor(Math.random() * 10000)}`,
      name: this.newLbName || 'New Load Balancer',
      status: 'Provisioning',
      ipAddress: 'Pending',
      region: this.newLbRegion,
      type: this.newLbType as any,
      ports: [80],
      backendServers: 0,
      uptime: '0s',
      created: new Date(),
      metrics: { requestsPerSecond: [], latency: [], connections: [] },
      logs: [{ timestamp: new Date(), level: 'Info', message: 'Provisioning started' }]
    };

    this.loadBalancers.unshift(newLb);
    this.applyFilters();
    this.selectLoadBalancer(newLb);
    this.closeCreateModal();
  }

  getStatusColor(status: string): string {
    switch (status) {
      case 'Active': return 'bg-emerald-100 text-emerald-800 border-emerald-200';
      case 'Inactive': return 'bg-slate-100 text-slate-800 border-slate-200';
      case 'Provisioning': return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'Error': return 'bg-red-100 text-red-800 border-red-200';
      default: return 'bg-gray-100 text-gray-800';
    }
  }
}
